import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import { routes } from '@/lib/routes';
import { LoginDialog } from '@/components/auth/LoginDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { authClient } from '@/lib/authClient';
import { toast } from 'sonner';
import { Users, LogIn, Info } from 'lucide-react';

export function CoachWelcome() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const [loginOpen, setLoginOpen] = useState(false);
  // Pre-check (`platformMode`) only makes sense for the direct login path
  // (existing coaches/operators). For new coaches redeeming an invite code,
  // no account exists yet — skip the pre-check.
  const [loginPlatformCheck, setLoginPlatformCheck] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasNoRole, setHasNoRole] = useState(false);
  const wasLoggedInRef = useRef(!!user);

  // If user is already a coach or operator, redirect immediately to the right surface
  useEffect(() => {
    if (!user) {
      setHasNoRole(false);
      setChecking(false);
      return;
    }
    setChecking(true);
    Promise.all([
      api.coach.me().catch(() => null),
      api.me.get().catch(() => null),
    ])
      .then(([coachRes, meRes]) => {
        if (meRes?.isOperator) {
          navigate(routes.coach.operator.dashboard(), { replace: true });
        } else if (coachRes?.isCoach) {
          navigate(routes.coach.dashboard(), { replace: true });
        } else {
          setHasNoRole(true);
        }
      })
      .finally(() => setChecking(false));
  }, [user, navigate]);

  async function handleSignOut() {
    try {
      await authClient.signOut();
    } catch {
      toast.error('Abmelden fehlgeschlagen.');
      return;
    }
    setUser(null);
    setHasNoRole(false);
  }

  async function performRedeem() {
    setSubmitting(true);
    try {
      await api.coach.redeemInvite(code.trim());
      toast.success('Willkommen im Coach-Bereich!');
      navigate(routes.coach.dashboard(), { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Einlösen fehlgeschlagen.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-redeem if the user just logged in via the dialog and has a code typed
  useEffect(() => {
    const justLoggedIn = !wasLoggedInRef.current && !!user;
    wasLoggedInRef.current = !!user;
    if (justLoggedIn && code.trim() && !submitting) {
      performRedeem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function handleRedeem() {
    if (!user) {
      setLoginPlatformCheck(false);
      setLoginOpen(true);
      return;
    }
    if (!code.trim()) return;
    performRedeem();
  }

  function openDirectLogin() {
    setLoginPlatformCheck(true);
    setLoginOpen(true);
  }

  if (checking) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-sm text-muted-foreground">
        Lade…
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center px-4 py-12 bg-muted/20">
      <div className="w-full max-w-md space-y-4">
        {hasNoRole && user && (
          <div className="flex gap-3 rounded-xl border border-amber-500/40 bg-amber-50/40 p-4 dark:bg-amber-500/5">
            <Info size={18} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-400" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-100">
                Angemeldet als {user.email}, aber ohne Coach-Zugang.
              </p>
              <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                Wolltest du dich vielleicht für das Inneres-Team-Tool anmelden?{' '}
                <a
                  href="https://app.teamity.io"
                  className="font-medium underline underline-offset-2"
                >
                  app.teamity.io
                </a>
                . Oder lies einen Einladungs-Code ein, um Coach zu werden.
              </p>
              <button
                type="button"
                onClick={handleSignOut}
                className="text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950 dark:text-amber-100"
              >
                Abmelden und mit anderem Konto einloggen
              </button>
            </div>
          </div>
        )}

        <div className="space-y-6 rounded-2xl border border-border bg-background p-8">
          <div className="space-y-2 text-center">
            <div className="inline-flex w-12 h-12 rounded-full bg-primary/10 items-center justify-center text-primary">
              <Users size={20} />
            </div>
            <h1 className="text-xl font-semibold">Coach-Bereich</h1>
            <p className="text-sm text-muted-foreground">
              Mit Einladungs-Code starten oder mit bestehendem Konto anmelden.
            </p>
          </div>

          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium">Einladungs-Code</span>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="z.B. ABCD-1234-EFGH"
                className="mt-1 font-mono text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
              />
            </label>
            <Button
              onClick={handleRedeem}
              disabled={submitting || !code.trim()}
              className="w-full"
            >
              {user
                ? submitting
                  ? 'Wird eingelöst…'
                  : 'Code einlösen'
                : 'Anmelden und einlösen'}
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Noch kein Code? Schreib uns an{' '}
            <a
              href="mailto:hallo@teamity.io"
              className="text-primary underline underline-offset-2"
            >
              hallo@teamity.io
            </a>
            .
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-background p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Bereits ein Konto?</p>
              <p className="text-xs text-muted-foreground">
                Für Coaches, die schon angemeldet waren.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={openDirectLogin}
              className="shrink-0 gap-1.5"
            >
              <LogIn size={14} />
              Einloggen
            </Button>
          </div>
        </div>
      </div>

      <LoginDialog
        open={loginOpen}
        onOpenChange={setLoginOpen}
        platformMode={loginPlatformCheck}
      />
    </div>
  );
}
